import { useEffect, useState } from 'react';
import { ButtonBase, Card, CardContent, Grid, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router';
import type { IMyConfiguration } from '../../../../types';
import {
  EActionType,
  EConfigurationStateCode,
  ETabValue,
  EUrlParams,
} from '../../../data/Constants';
import { changeLandingTab } from '../../../store/states/ConfigurationSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/AppStore';
import { useTranslation } from 'react-i18next';
import { formatDateString, addStateValues } from '../../../store/Helpers/LandingHelper';
import ActionDialog from '../../../components/ActionDialog'; // Import ActionDialog directly
import { deleteConfigurationHelper } from '../../../services';

/**
 * Renders a single configuration card item for the My Configurations page.
 *
 * @param {Object} props - The component props
 * @param {IMyConfiguration} props.config - The configuration card item info
 * @param {number} props.index - The configuration index
 * @returns {JSX.Element} The rendered configuration card item
 */

interface ConfigurationCardItemProps {
  config: IMyConfiguration;
  index: number;
}

export const ConfigurationCardItem = ({ config, index }: ConfigurationCardItemProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  // State to manage dialog visibility
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // State to track if the screen is desktop size
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1450);

  /**
   * Updates the isDesktop state based on window width.
   */
  const updateMedia = () => {
    setIsDesktop(window.innerWidth > 1450);
  };

  /**
   * Add the resize event on initial load and remove this event on component destroy.
   * This ensures the card layout adapts to screen size changes.
   */
  useEffect(() => {
    window.addEventListener('resize', updateMedia);
    return () => window.removeEventListener('resize', updateMedia);
  }, []);

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = () => {
    setShowDeleteDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
  };

  // Handle the delete action when confirmed
  const handleConfirmDelete = () => {
    deleteConfigurationHelper(config, dispatch, t)();
    setShowDeleteDialog(false);
  };

  // Render the configuration card item
  return (
    <>
      <Grid
        key={config.configurationId}
        size={{
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6,
          xl: 6,
        }}
      >
        <Card
          sx={{
            border: '1px solid grey',
            position: 'relative',
            backgroundColor:
              config.state === Number(EConfigurationStateCode.UnAvailable) ? '#e0e0e0' : 'white',
          }}
          className="minh-100"
          id={'mc-' + index}
          data-testid={'mcCard' + index}
        >
          <CardContent sx={{ position: 'relative' }}>
            <DeleteIcon
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'red',
                cursor: 'pointer',
                zIndex: 1,
              }}
              onClick={handleOpenDeleteDialog}
            />
            <div className="cardLayoutMainDiv">
              <div>
                <img
                  src="public\png\SamplePMImage.png"
                  style={{ height: '80px', width: '80px', marginLeft: '5px', marginTop: '10px' }}
                  alt="sample-image"
                />
              </div>

              <div style={{ display: 'block', marginLeft: '10px', width: '100%' }}>
                <div className="cardLayoutChildProdDiv">
                  <Tooltip placement="bottom-start" title={config.productDescription}>
                    <div>
                      <span className="product-description">{config.productDescription}</span>
                      <span className="product-id">&nbsp;({config.productId})</span>
                    </div>
                  </Tooltip>
                </div>

                <div>
                  <span className="configurationName">
                    <b>{t('labels.name')}: </b>
                    {/* Clicking the config name navigates to the configuration details page and sets the landing tab */}
                    <ButtonBase
                      component="span"
                      className="configName"
                      onClick={() => {
                        dispatch(changeLandingTab({ value: true, tabValue: ETabValue.Product }));
                        navigate('/?' + EUrlParams.ConfigurationId + '=' + config.configurationId, {
                          replace: true,
                        });
                      }}
                    >
                      {config.name}
                    </ButtonBase>
                  </span>
                </div>

                <div style={{ display: 'flex' }}>
                  <div className="cardLayoutChildDiv w-40">
                    <span>
                      <b>{t('labels.Id')}:</b> {config.configurationId.substring(0, 18)}
                    </span>
                    <span style={{ display: 'flex' }}>
                      <b>{t('labels.country')}</b>
                      <span>:&nbsp;</span>
                      <span
                        className={
                          config.countryName.length > 18 && !isDesktop ? 'overflow-country' : ''
                        }
                      >
                        {config.countryName}
                      </span>
                      ({config.countryCode})
                    </span>
                    <span>
                      <b>{t('labels.state')}:</b> {addStateValues(config.state)}
                    </span>
                  </div>

                  <div className="cardLayoutChildDiv w-60">
                    <span>
                      <b>{t('labels.createdOn')}:</b> {formatDateString(config.createdOn) + ' UTC'}
                    </span>
                    <span>
                      <b>{t('labels.modifiedOn')}:</b>&nbsp;
                      {formatDateString(config.modifiedOn) + ' UTC'}
                    </span>
                    <span>
                      <b>{t('labels.application')}:</b> {config.createdFromName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Render ActionDialog conditionally based on showDeleteDialog state */}
      {showDeleteDialog && (
        <ActionDialog
          showDialog={showDeleteDialog}
          onCloseDialog={handleCloseDeleteDialog}
          errorMessage={''}
          dialog={t('Confirmation.deleteConfirmation')}
          action={handleConfirmDelete}
          dialogType={EActionType.Confirm}
        />
      )}
    </>
  );
};
